import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Building2, Users, Receipt, Wallet, Bell, CheckCircle2, Clock, Coffee,
  CalendarDays, ArrowRight, Loader2, ClipboardList, Package, Wrench,
  BadgeCheck, TrendingUp, Sparkles, Sun, Moon, CloudSun, PlaneTakeoff,
  Briefcase, LogIn, LogOut, PlayCircle, AlertCircle, FileText, MessageCircle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { dashboardAPI } from '../lib/api';
import api from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { TimeTrackingWidget } from '../components/TimeTrackingWidget';
import { StaffNewsTicker } from '../components/DashboardNewsTicker';

// Palette Élégance Africaine
const PALETTE = {
  forest: '#2B3A2C',
  forestSoft: '#3E4F3F',
  copper: '#B85C38',
  copperSoft: '#D07A55',
  sand: '#F5EFE1',
  ivory: '#FBF7EE',
};

const fmtNumber = (n) => (Number(n || 0)).toLocaleString('fr-FR');
const fmtMoney = (n, cur = 'XOF') => formatCurrency(n || 0, cur);

const greetingByHour = () => {
  const h = new Date().getHours();
  if (h < 12) return { text: 'Bonjour', Icon: Sun };
  if (h < 18) return { text: 'Bon après-midi', Icon: CloudSun };
  return { text: 'Bonsoir', Icon: Moon };
};

// Ring gauge SVG - solde congés / présence
const RingGauge = ({ value = 0, max = 30, size = 84, color = PALETTE.copper, label, sublabel }) => {
  const pct = Math.min(1, Math.max(0, value / (max || 1)));
  const R = (size - 12) / 2;
  const C = 2 * Math.PI * R;
  return (
    <div className="flex items-center gap-3">
      <div className="relative" style={{ width: size, height: size }}>
        <svg viewBox={`0 0 ${size} ${size}`} className="w-full h-full -rotate-90">
          <circle cx={size / 2} cy={size / 2} r={R} fill="none" stroke="#F5EFE1" strokeWidth="8" />
          <circle
            cx={size / 2} cy={size / 2} r={R}
            fill="none" stroke={color} strokeWidth="8" strokeLinecap="round"
            strokeDasharray={`${C * pct} ${C}`}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-lg font-extrabold" style={{ color: PALETTE.forest }}>
            {typeof value === 'number' ? value.toFixed(value % 1 === 0 ? 0 : 1) : value}
          </span>
        </div>
      </div>
      <div>
        <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">{label}</p>
        <p className="text-[11px] text-gray-400 mt-0.5">{sublabel}</p>
      </div>
    </div>
  );
};

// KPI compact
const AgentKpi = ({ icon: Icon, label, value, sub, tone = 'copper', to }) => {
  const tones = {
    copper: { bg: 'bg-[#B85C38]/10', color: 'text-[#B85C38]', border: 'border-[#B85C38]/25' },
    forest: { bg: 'bg-[#2B3A2C]', color: 'text-white', border: 'border-[#2B3A2C]/30' },
    emerald: { bg: 'bg-emerald-100', color: 'text-emerald-700', border: 'border-emerald-200' },
    amber: { bg: 'bg-amber-100', color: 'text-amber-700', border: 'border-amber-200' },
    rose: { bg: 'bg-rose-100', color: 'text-rose-700', border: 'border-rose-200' },
    sky: { bg: 'bg-sky-100', color: 'text-sky-700', border: 'border-sky-200' },
  };
  const t = tones[tone] || tones.copper;
  const Card = to ? Link : 'div';
  const props = to ? { to } : {};
  return (
    <Card
      {...props}
      className={`bg-white rounded-2xl border ${t.border} shadow-sm p-4 hover:shadow-md transition group block`}
      data-testid={`agent-kpi-${label?.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${t.bg}`}>
        <Icon className={`w-5 h-5 ${t.color}`} strokeWidth={2.2} />
      </div>
      <p className="text-[11px] uppercase text-gray-500 font-semibold tracking-wide">{label}</p>
      <p className="text-xl font-bold text-[#2B3A2C] mt-1 tracking-tight truncate">{value}</p>
      {sub && <p className="text-[11px] text-gray-500 mt-1 truncate">{sub}</p>}
    </Card>
  );
};

const STATE_LABEL = {
  off: { label: 'Non pointé', className: 'bg-gray-100 text-gray-700' },
  at_office: { label: 'Au bureau', className: 'bg-emerald-100 text-emerald-800' },
  on_mission: { label: 'En mission', className: 'bg-sky-100 text-sky-800' },
  on_break: { label: 'En pause', className: 'bg-indigo-100 text-indigo-800' },
};

const AgentDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);          // stats agence (lecture seule)
  const [myTasks, setMyTasks] = useState([]);
  const [tasksAll, setTasksAll] = useState([]);
  const [balance, setBalance] = useState(null);
  const [myRequests, setMyRequests] = useState([]);
  const [notifs, setNotifs] = useState({ items: [], unread: 0 });
  const [ttStatus, setTtStatus] = useState({ state: 'off', entries: [], enabled: false });
  const [submitting, setSubmitting] = useState(false);
  const [shipStats, setShipStats] = useState(null);
  const [repairReqs, setRepairReqs] = useState([]);

  const fetchAll = useCallback(async () => {
    try {
      const [s, tks, bal, mine, nRes, tt, shp, rrq] = await Promise.all([
        dashboardAPI.getStats().catch(() => ({ data: null })),
        api.get('/tasks').catch(() => ({ data: [] })),
        api.get('/me/leave-balance').catch(() => ({ data: null })),
        api.get('/leave-requests/mine').catch(() => ({ data: [] })),
        api.get('/notifications/mine').catch(() => ({ data: { items: [], unread: 0 } })),
        api.get('/time-tracking/my-status').catch(() => ({ data: { state: 'off', entries: [], enabled: false } })),
        api.get('/shipments/stats').catch(() => ({ data: null })),
        api.get('/repair-requests').catch(() => ({ data: [] })),
      ]);
      setStats(s.data);
      const all = tks.data || [];
      setTasksAll(all);
      setMyTasks(
        all
          .filter((t) => t.status !== 'done')
          .sort((a, b) => new Date(a.due_date || 0) - new Date(b.due_date || 0))
          .slice(0, 5),
      );
      setBalance(bal.data);
      setMyRequests(mine.data || []);
      setNotifs(nRes.data || { items: [], unread: 0 });
      setTtStatus(tt.data || { state: 'off', entries: [], enabled: false });
      setShipStats(shp.data);
      setRepairReqs((rrq.data || []).slice(0, 5));
    } catch (e) {
      // silent - éviter de spam le toast en mode agent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleTt = async (action) => {
    setSubmitting(true);
    try {
      const res = await api.post(`/time-tracking/${action}`, {});
      setTtStatus({ state: res.data.state, entries: res.data.entries, enabled: true });
      toast.success('Pointage enregistré');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action refusée');
    } finally {
      setSubmitting(false);
    }
  };

  const greet = useMemo(() => greetingByHour(), []);
  const today = useMemo(
    () => new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' }),
    []
  );

  const upcomingLeave = useMemo(
    () => (myRequests || []).find((r) => r.status === 'approved' && r.request_type === 'leave' && new Date(r.start_date) >= new Date(new Date().toDateString())),
    [myRequests]
  );
  const pendingLeave = useMemo(
    () => (myRequests || []).filter((r) => r.status === 'pending'),
    [myRequests]
  );

  const myTasksCount = tasksAll.filter((t) => t.status !== 'done').length;
  const dueTodayCount = tasksAll.filter((t) => {
    if (!t.due_date || t.status === 'done') return false;
    const d = new Date(t.due_date);
    const now = new Date();
    return d.toDateString() === now.toDateString();
  }).length;
  const pendingRepairsForMe = repairReqs.filter((r) => r.status === 'en_attente').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#B85C38]" />
      </div>
    );
  }

  const ttState = STATE_LABEL[ttStatus.state] || STATE_LABEL.off;
  const canClockIn = ttStatus.state === 'off';
  const canClockOut = ttStatus.state === 'at_office' || ttStatus.state === 'on_mission';
  const canMission = ttStatus.state === 'at_office';
  const canReturnMission = ttStatus.state === 'on_mission';
  const canBreak = ttStatus.state === 'at_office';
  const canEndBreak = ttStatus.state === 'on_break';

  return (
    <div className="space-y-6 pb-10" data-testid="agent-dashboard-page">
      <StaffNewsTicker />
      {/* HERO ÉLÉGANCE AFRICAINE */}
      <div
        className="relative overflow-hidden rounded-3xl p-6 lg:p-8 shadow-lg"
        style={{
          background: `linear-gradient(135deg, ${PALETTE.forest} 0%, ${PALETTE.forestSoft} 55%, #4A5E4B 100%)`,
        }}
      >
        {/* Motif wax subtil */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-15 pointer-events-none"
          style={{
            backgroundImage:
              "url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22120%22 height=%22120%22 viewBox=%220 0 120 120%22><g fill=%22none%22 stroke=%22%23F5EFE1%22 stroke-width=%221%22><circle cx=%2260%22 cy=%2260%22 r=%2250%22/><circle cx=%2260%22 cy=%2260%22 r=%2234%22/><circle cx=%2260%22 cy=%2260%22 r=%2218%22/><path d=%22M60 10 L60 110 M10 60 L110 60%22/></g></svg>')",
            backgroundSize: '160px 160px',
          }}
        />
        {/* Halo cuivre */}
        <div
          aria-hidden
          className="absolute -top-24 -right-24 w-96 h-96 rounded-full opacity-30 blur-3xl"
          style={{ background: PALETTE.copper }}
        />

        <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div className="text-white">
            <div className="flex items-center gap-2 text-[#F5EFE1]/90 text-xs uppercase font-semibold tracking-widest">
              <greet.Icon className="w-4 h-4" /> {greet.text}
            </div>
            <h1 className="text-3xl lg:text-4xl font-extrabold tracking-tight mt-1">
              {(user?.name || 'Collaborateur').split(' ')[0]}
              <span className="ml-2 text-[#F5EFE1]/90 font-medium text-xl">
                · {user?.employee_id || (user?.role === 'agent' ? 'Agent' : 'Collaborateur')}
              </span>
            </h1>
            <p className="text-[#F5EFE1]/80 mt-2 text-sm capitalize">{today}</p>
          </div>

          {/* Badge statut de présence rapide (le module complet est juste dessous) */}
          <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl px-4 py-3 flex items-center gap-3">
            <Clock className="w-5 h-5 text-[#F5EFE1]/70" />
            <div>
              <p className="text-[10px] text-[#F5EFE1]/80 uppercase font-semibold tracking-wide">Ma présence</p>
              <Badge className={`mt-1 ${ttState.className} border-0`} data-testid="agent-presence-badge">{ttState.label}</Badge>
            </div>
          </div>
        </div>
      </div>

      {/* Module de pointage complet (horloge live + boutons + métriques) */}
      <TimeTrackingWidget />

      {/* RANG 1 : KPIs personnels (RH + tâches) */}
      <div>
        <h2 className="text-xs uppercase tracking-widest font-bold text-[#2B3A2C]/70 mb-3 flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5 text-[#B85C38]" /> Mon espace du jour
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <AgentKpi
            icon={ClipboardList}
            label="Mes tâches"
            value={fmtNumber(myTasksCount)}
            sub={dueTodayCount ? `${dueTodayCount} pour aujourd'hui` : 'À jour'}
            tone="forest"
            to="/tasks"
          />
          <AgentKpi
            icon={CalendarDays}
            label="Solde congés"
            value={`${(balance?.balance ?? 0).toFixed(1)} j`}
            sub={balance?.pending_requested ? `${balance.pending_requested} j en attente` : `Cumul ${balance?.taken_this_year || 0} j pris`}
            tone="copper"
            to="/my-hr"
          />
          <AgentKpi
            icon={Bell}
            label="Notifications"
            value={fmtNumber(notifs.unread)}
            sub={notifs.unread ? 'Non lues' : 'Tout est lu'}
            tone={notifs.unread ? 'rose' : 'emerald'}
          />
          <AgentKpi
            icon={Wrench}
            label="Demandes réparation"
            value={fmtNumber(pendingRepairsForMe)}
            sub="En attente de traitement"
            tone="amber"
            to="/repair-requests"
          />
        </div>
      </div>

      {/* RANG 2 : Vue agence (lecture seule) */}
      <div>
        <h2 className="text-xs uppercase tracking-widest font-bold text-[#2B3A2C]/70 mb-3 flex items-center gap-2">
          <TrendingUp className="w-3.5 h-3.5 text-[#B85C38]" /> Activité de l&apos;agence
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <AgentKpi
            icon={Building2}
            label="Biens gérés"
            value={fmtNumber(stats?.total_properties)}
            sub={`${fmtNumber(stats?.properties_rented)} loués`}
            tone="sky"
            to="/properties"
          />
          <AgentKpi
            icon={Users}
            label="Locataires"
            value={fmtNumber(stats?.total_clients)}
            sub="Base clients"
            tone="emerald"
            to="/clients"
          />
          <AgentKpi
            icon={Wallet}
            label="Encaissé (mois)"
            value={fmtMoney(stats?.monthly_collected_amount)}
            sub={`${(stats?.collection_rate_percentage || 0).toFixed(1)}% du dû`}
            tone="copper"
            to="/rent-payments"
          />
          <AgentKpi
            icon={Package}
            label="Colis actifs"
            value={fmtNumber(shipStats?.active_count || shipStats?.in_transit_count || 0)}
            sub={shipStats?.today_count ? `${shipStats.today_count} aujourd'hui` : 'Suivi & remise'}
            tone="forest"
            to="/shipments"
          />
        </div>
      </div>

      {/* RANG 3 : Tâches / Notifications / Congés */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Mes tâches du jour */}
        <div className="lg:col-span-5 bg-white rounded-2xl border border-[#B85C38]/15 shadow-sm p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold text-[#2B3A2C] text-lg flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-[#B85C38]" /> Mes tâches
            </h2>
            <Link to="/tasks" className="text-xs text-[#B85C38] font-semibold hover:underline flex items-center gap-1">
              Voir tout <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="space-y-2">
            {myTasks.length === 0 ? (
              <div className="text-center py-8">
                <BadgeCheck className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Aucune tâche en cours. Bien joué !</p>
              </div>
            ) : myTasks.map((t) => {
              const d = t.due_date ? new Date(t.due_date) : null;
              const isOverdue = d && d < new Date() && t.status !== 'done';
              const isTodayTask = d && d.toDateString() === new Date().toDateString();
              return (
                <Link
                  key={t.id}
                  to="/tasks"
                  className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-[#F5EFE1]/60 transition border border-transparent hover:border-[#B85C38]/20"
                >
                  <div className={`w-11 h-11 rounded-xl flex flex-col items-center justify-center flex-shrink-0 ${
                    isOverdue ? 'bg-rose-100 text-rose-700' : isTodayTask ? 'bg-[#B85C38] text-white' : 'bg-[#F5EFE1] text-[#B85C38]'
                  }`}>
                    <span className="text-sm font-bold leading-none">{d ? d.getDate() : '—'}</span>
                    <span className="text-[9px] font-semibold uppercase mt-0.5">{d ? d.toLocaleDateString('fr-FR', { month: 'short' }).replace('.', '') : ''}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#2B3A2C] truncate">{t.title}</p>
                    <p className="text-xs text-gray-500 truncate">{t.description || t.category || '—'}</p>
                  </div>
                  {t.priority && (
                    <Badge className={`text-[10px] ${
                      t.priority === 'urgent' ? 'bg-rose-100 text-rose-700' :
                      t.priority === 'high' ? 'bg-amber-100 text-amber-700' :
                      'bg-gray-100 text-gray-600'
                    } border-0`}>
                      {t.priority}
                    </Badge>
                  )}
                </Link>
              );
            })}
          </div>
        </div>

        {/* Notifications */}
        <div className="lg:col-span-4 bg-white rounded-2xl border border-[#B85C38]/15 shadow-sm p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold text-[#2B3A2C] text-lg flex items-center gap-2">
              <Bell className="w-5 h-5 text-[#B85C38]" /> Notifications
            </h2>
            {notifs.unread > 0 && (
              <Badge className="bg-rose-500 text-white border-0">{notifs.unread}</Badge>
            )}
          </div>
          <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1">
            {(notifs.items || []).length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-6">Aucune notification.</p>
            ) : (notifs.items || []).slice(0, 6).map((n) => (
              <div key={n.id} className={`flex items-start gap-2 p-2 rounded-lg ${n.read ? 'bg-white' : 'bg-[#B85C38]/5 border border-[#B85C38]/15'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${n.read ? 'bg-gray-100' : 'bg-[#B85C38]/15'}`}>
                  <MessageCircle className={`w-4 h-4 ${n.read ? 'text-gray-400' : 'text-[#B85C38]'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-xs truncate ${n.read ? 'text-gray-600' : 'font-semibold text-[#2B3A2C]'}`}>
                    {n.title || 'Notification'}
                  </p>
                  <p className="text-[11px] text-gray-500 line-clamp-2">{n.body || n.message || ''}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Solde congés + demandes */}
        <div
          className="lg:col-span-3 rounded-2xl shadow-lg p-5 text-white relative overflow-hidden"
          style={{ background: `linear-gradient(160deg, ${PALETTE.copper} 0%, ${PALETTE.copperSoft} 100%)` }}
        >
          <div
            aria-hidden
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage:
                "url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22100%22 height=%22100%22 viewBox=%220 0 100 100%22><g fill=%22none%22 stroke=%22white%22 stroke-width=%221%22><path d=%22M0 50 Q25 20 50 50 T100 50%22/><path d=%22M0 30 Q25 60 50 30 T100 30%22/></g></svg>')",
              backgroundSize: '140px 140px',
            }}
          />
          <div className="relative">
            <h2 className="font-bold text-lg mb-3 flex items-center gap-2">
              <CalendarDays className="w-5 h-5" /> Mes congés
            </h2>
            <RingGauge
              value={balance?.balance || 0}
              max={30}
              color="#FFFFFF"
              label={<span className="text-white/90">Solde restant</span>}
              sublabel={<span className="text-white/70">sur 30 j / an</span>}
            />
            <div className="mt-4 space-y-1.5 text-xs">
              <div className="flex items-center justify-between">
                <span className="text-white/80">Pris cette année</span>
                <span className="font-bold">{(balance?.taken_this_year || 0).toFixed(1)} j</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-white/80">En attente</span>
                <span className="font-bold">{(balance?.pending_requested || 0).toFixed(1)} j</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-white/80">À venir (validé)</span>
                <span className="font-bold">{(balance?.approved_upcoming || 0).toFixed(1)} j</span>
              </div>
            </div>
            {upcomingLeave && (
              <div className="mt-3 bg-white/15 backdrop-blur-sm rounded-lg p-2 text-[11px]">
                <p className="font-semibold">Prochain congé :</p>
                <p className="text-white/90">Du {new Date(upcomingLeave.start_date).toLocaleDateString('fr-FR')} au {new Date(upcomingLeave.end_date).toLocaleDateString('fr-FR')}</p>
              </div>
            )}
            <Link
              to="/my-hr"
              className="mt-4 flex items-center justify-center gap-2 bg-white text-[#B85C38] rounded-xl py-2 text-xs font-bold hover:bg-white/90 transition"
              data-testid="agent-open-hr-btn"
            >
              Mon espace RH <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      </div>

      {/* RANG 4 : Raccourcis rapides */}
      <div
        className="rounded-2xl shadow-lg p-5 text-white relative overflow-hidden"
        style={{ background: `linear-gradient(120deg, ${PALETTE.forest} 0%, ${PALETTE.forestSoft} 100%)` }}
      >
        <div
          aria-hidden
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22140%22 height=%22140%22 viewBox=%220 0 140 140%22><g fill=%22none%22 stroke=%22%23F5EFE1%22 stroke-width=%221%22><rect x=%2210%22 y=%2210%22 width=%22120%22 height=%22120%22 rx=%2210%22/><circle cx=%2270%22 cy=%2270%22 r=%2240%22/><path d=%22M30 70 L110 70 M70 30 L70 110%22/></g></svg>')",
            backgroundSize: '160px 160px',
          }}
        />
        <div className="relative">
          <h2 className="font-bold text-lg mb-4 text-[#F5EFE1] flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#B85C38]" /> Actions rapides
          </h2>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2.5">
            {[
              { to: '/rent-payments?new=1', icon: Receipt, label: 'Encaisser loyer' },
              { to: '/clients', icon: Users, label: 'Fiche locataire' },
              { to: '/properties', icon: Building2, label: 'Consulter biens' },
              { to: '/shipments', icon: Package, label: 'Module colis' },
              { to: '/repair-requests', icon: Wrench, label: 'Réparations' },
              { to: '/my-hr', icon: Briefcase, label: 'Mes congés / paie' },
            ].map((a, i) => (
              <Link
                key={i}
                to={a.to}
                className="group bg-white/10 hover:bg-white/20 border border-white/15 rounded-xl p-3 text-center transition backdrop-blur-sm"
                data-testid={`agent-quick-${i}`}
              >
                <a.icon className="w-5 h-5 mx-auto mb-1.5 text-[#F5EFE1] group-hover:text-white group-hover:scale-110 transition" />
                <p className="text-[10px] text-[#F5EFE1] leading-tight font-semibold">{a.label}</p>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentDashboard;
